import PropTypes from "prop-types";
import React, { lazy, Suspense, useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ToastContainer } from "react-toastify";
import { ErrorBoundary } from "react-error-boundary";
import EventTabNav from "./EventTabNav";
import TabPlaceholder from "./TabPlaceholder";
import LastEditIndicator from "./LastEditIndicator";
import EventHeader, { EventHeaderField, EventStatusBadge } from "./EventHeader";
import TagsField from "./TagsField";
import ActionsMenu from "./ActionsMenu";
import EventSeriesBanner from "../../ConfirmShow/components/EventSeriesBanner";
import OfferPerformerBanner from "./offers/OfferPerformerBanner";
import { attachPerformerToOffer } from "../../../shared/offerLineupPerformer";
import PageLayoutSelector from "../../shared/PageLayoutSelector/PageLayoutSelector";
import useTabData, { forgetTabData } from "../hooks/useTabData";
import { EVENT_TABS, DEFAULT_TAB, TAB_KEYS, tabsForType } from "../constants/tabs";
import "../styles/eventPageStyles.css";
import "../../../shared/marketing/marketingShared.css";
import { toastError } from "../../../shared/toastHelper";
import { ticketCountsEventLabel } from "../../ConfirmShow/utils/sendTicketCountsNow";
import { showTimeLabel } from "../../../shared/showTimeLabel";
import {
  Announcement01, Badge, Building07, Button, CalendarDate, Edit01, LayoutGrid01, MagicWand02, MarkerPin02, MusicNote01, Tag, User01, Users01
} from "@opendate-app/setlist";

// Tab panels and the Edit Details modal are code-split so the page shell renders
// immediately; each tab's JS (and the heavy edit modal) loads only when activated.
const DashboardTab = lazy(() => import("./dashboard/DashboardTab"));
const TicketsTab = lazy(() => import("./tickets/TicketsTab"));
const ContactsTab = lazy(() => import("./contacts/ContactsTab"));
const AssetsTab = lazy(() => import("./assets/AssetsTab"));
const ShowDetailsTab = lazy(() => import("./show_details/ShowDetailsTab"));
const Notes = lazy(() => import("../../Notes/components/Notes"));
const OffersTab = lazy(() => import("./offers/OffersTab"));
const SettlementsTab = lazy(() => import("./settlements/SettlementsTab"));
const FinancesTab = lazy(() => import("./finances/FinancesTab"));
const ProfitAndLossTab = lazy(() => import("./profit_and_loss/ProfitAndLossTab"));
const StaffingTab = lazy(() => import("./StaffingTab"));
const EventAttributionTab = lazy(() => import("./attribution/EventAttributionTab"));
const AudienceInsights = lazy(() => import("../../../shared/marketing/AudienceInsights"));
const BuyerLocationCard = lazy(() => import("../../../shared/marketing/BuyerLocationCard"));
const TrafficConversionCard = lazy(() => import("../../../shared/marketing/TrafficConversionCard"));
const SourcesLinksCard = lazy(() => import("../../../shared/marketing/SourcesLinksCard"));
const ComparableEvents = lazy(() => import("../../../shared/marketing/ComparableEvents"));
const MetaAdSpendSummary = lazy(() => import("../../../shared/marketing/MetaAdSpendSummary"));
const EventMessages = lazy(() => import("../../../shared/marketing/EventMessages"));
const EventDetailsApp = lazy(() => import("../../EventDetails/startup/EventDetailsApp"));

const TabLoader = () => (
  <div className="event-tab-loading">
    Loading…
  </div>
);

const TabErrorFallback = () => (
  <div className="event-tab-loading" style={{ padding: "48px 16px", textAlign: "center", color: "var(--sl-text-tertiary)" }}>
    Couldn't load this tab. Refresh to try again.
  </div>
);

const tabFromHash = () => {
  const hash = (typeof window !== "undefined" ? window.location.hash : "").replace("#", "");
  return TAB_KEYS.includes(hash) ? hash : DEFAULT_TAB;
};

const EventPage = (props) => {
  const { editDetailsUrl } = props;
  // isRental is toggled inline from the setup checklist, so confirm is state:
  // the change reflects live in the Rental Invoices card and survives tab revisits.
  const [confirm, setConfirm] = useState(props.confirm);
  const [activeTab, setActiveTab] = useState(tabFromHash);
  const [editProps, setEditProps] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [missingPerformer, setMissingPerformer] = useState(props.offersMissingPerformer);
  const [offersTabVersion, setOffersTabVersion] = useState(0);

  // Clamp the active tab to the visible set for this event type (Holds hide tabs
  // like Tickets/Settlements). Computed before hooks so the URL gate is stable.
  // Finances is hidden for roles the event_finances endpoint 403s (guests,
  // promoters, consumers), who would otherwise only ever see a load error.
  const staffingOnHold = props.staffingTab === "schedule";
  const visibleTabs = tabsForType(confirm.type, { staffing: staffingOnHold, attribution: Boolean(props.attribution) }).filter(
    (t) => (t.key !== "finances" || props.currentUser?.canManageFinanceItems)
      && (t.key !== "staffing" || props.staffingTab !== "hidden")
  );
  const effectiveTab = visibleTabs.some((t) => t.key === activeTab) ? activeTab : visibleTabs[0].key;

  // Dashboard data is fetched after first paint (it carries the heavy P&L/forecast
  // computation); the page shell paints from props immediately. Cached on revisit.
  const dashboard = useTabData(effectiveTab === "dashboard" ? props.dashboardUrl : null);
  const tickets = useTabData(effectiveTab === "tickets" ? props.ticketsUrl : null);
  const assets = useTabData(effectiveTab === "assets" ? props.assetsUrl : null);
  const showDetails = useTabData(effectiveTab === "show_details" ? props.showDetailsUrl : null);
  const notes = useTabData(effectiveTab === "notes" ? props.notesUrl : null);
  const offers = useTabData(effectiveTab === "offers" ? props.offersUrl : null);
  const settlements = useTabData(effectiveTab === "settlements" ? props.settlementsUrl : null);
  const finances = useTabData(effectiveTab === "finances" ? props.financesUrl : null);
  const marketing = useTabData(effectiveTab === "marketing" ? props.marketingUrl : null);
  const profitAndLoss = useTabData(effectiveTab === "profit_and_loss" ? props.profitAndLossUrl : null);
  const staffing = useTabData(effectiveTab === "staffing" ? props.staffingUrl : null);

  useEffect(() => {
    const onHashChange = () => setActiveTab(tabFromHash());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  // Notes keeps its own list state; mirror changes back into the tab-data cache
  // so leaving and returning to the tab re-seeds with the latest notes rather
  // than the stale payload fetched on first visit.
  const notesSetData = notes.setData;
  const handleNotesChanged = useCallback((updatedNotes) => {
    notesSetData((prev) => (prev ? { ...prev, notes: updatedNotes } : prev));
  }, [notesSetData]);

  // The Links card edits the website inline; mirror it back into the dashboard
  // tab-data cache so revisiting the tab shows the saved value, not the stale one.
  const dashboardSetData = dashboard.setData;
  const handleWebsiteChanged = useCallback((website) => {
    dashboardSetData((prev) => (prev ? { ...prev, links: { ...prev.links, website } } : prev));
  }, [dashboardSetData]);
  const handleContactsChanged = useCallback((updatedContacts) => {
    dashboardSetData((prev) => (prev ? { ...prev, contacts: updatedContacts } : prev));
  }, [dashboardSetData]);
  const handleLineupChanged = useCallback(() => {
    forgetTabData(props.offersUrl);
    return axios.get(props.dashboardUrl)
      .then((res) => { dashboardSetData(() => res.data); })
      .catch(() => toastError("Artist added, but the lineup didn’t refresh. Reload to see it."));
  }, [props.offersUrl, props.dashboardUrl, dashboardSetData]);

  // Room-rental toggle in the setup checklist. Optimistic; reverts on failure.
  // Same target/param as the Edit Details "This is a room rental" checkbox.
  const handleRentalToggle = useCallback((next) => {
    setConfirm((c) => ({ ...c, isRental: next }));
    axios.defaults.headers.common["X-CSRF-TOKEN"] = props.csrfToken;
    axios.patch(editDetailsUrl, { confirm: { is_rental: next } })
      .catch(() => {
        setConfirm((c) => ({ ...c, isRental: !next }));
        toastError("Couldn't update room rental. Try again.");
      });
  }, [editDetailsUrl, props.csrfToken]);

  const selectTab = (key) => {
    setActiveTab(key);
    if (typeof window !== "undefined") window.location.hash = key;
  };

  const label = EVENT_TABS.find((t) => t.key === effectiveTab)?.label || effectiveTab;
  const genres = confirm.genres || [];
  const tags = confirm.tags || [];
  // Tickets tab shows the on-sale status as a green badge (no standalone On Sale tab).
  // A Hold can arrive with a hash for a hidden tab (e.g. #tickets from a Confirm link);
  // effectiveTab (computed above) already clamps to the first visible tab in that case.
  const flaggedOffers = missingPerformer?.offers || [];
  const tabs = visibleTabs.map((t) => {
    if (t.key === "tickets" && confirm.status === "On Sale") return { ...t, badge: "On Sale" };
    if (t.key === "offers" && flaggedOffers.length > 0) return { ...t, alert: String(flaggedOffers.length) };
    if (t.key === "settlements" && flaggedOffers.length > 0) return { ...t, dot: true };
    return t;
  });

  const headerMeta = [
    confirm.startTime && { icon: CalendarDate, label: showTimeLabel(confirm.startTime, confirm.doorTime, confirm.timeZone) },
    confirm.venueName && { icon: Building07, label: confirm.venueName },
    confirm.venueConfig && confirm.venueConfig !== confirm.venueName && { icon: Building07, label: confirm.venueConfig },
    confirm.venueLocation && { icon: MarkerPin02, label: confirm.venueLocation },
    confirm.ageRestriction && { icon: Users01, label: confirm.ageRestriction },
    confirm.buyer && { icon: User01, label: `Buyer · ${confirm.buyer}` },
    confirm.promoter && { icon: Announcement01, label: `Promoter · ${confirm.promoter}` },
    confirm.eventTemplate && { icon: LayoutGrid01, label: `Template · ${confirm.eventTemplate}` },
  ].filter(Boolean);

  const markPerformerAttached = useCallback((offerId, performerId) => {
    setMissingPerformer((prev) => (prev ? {
      ...prev,
      offers: prev.offers.filter((o) => o.id !== offerId),
      performers: prev.performers.map((p) => (p.id === performerId ? { ...p, offer: { id: offerId } } : p)),
    } : prev));
  }, []);

  const attachFromBanner = (offerId, performer) => (
    attachPerformerToOffer(props.team.id, offerId, performer.id)
      .then(() => {
        markPerformerAttached(offerId, performer.id);
        setOffersTabVersion((v) => v + 1);
      })
      .catch(() => toastError(`Couldn't attach ${performer.artistable?.name} to the offer. Please try again.`))
  );

  // Fetch the (heavy) Edit Details props only when the user first opens the modal.
  const openEditDetails = () => {
    if (editProps) { setEditOpen(true); return; }
    if (editLoading) return;
    setEditLoading(true);
    axios.get(props.editDetailsPropsUrl)
      .then((res) => { setEditProps(res.data); setEditOpen(true); })
      .catch(() => window.location.assign(editDetailsUrl))
      .finally(() => setEditLoading(false));
  };

  const renderTab = () => {
    if (effectiveTab === "dashboard") {
      if (dashboard.loading) return <TabLoader />;
      if (dashboard.error || !dashboard.data) {
        return (
          <div className="event-tab-loading">
            Couldn’t load the dashboard. Refresh to try again.
          </div>
        );
      }
      return (
        <DashboardTab
          eventType={confirm.type}
          holdDates={confirm.holdDates}
          dashboard={dashboard.data.dashboard}
          lineup={dashboard.data.lineup}
          vertical={dashboard.data.vertical}
          contacts={dashboard.data.contacts}
          timeline={dashboard.data.timeline}
          links={dashboard.data.links}
          confirm={confirm}
          rentalInvoicesUrl={props.rentalInvoicesUrl}
          teamId={props.team.id}
          confirmId={confirm.id}
          csrfToken={props.csrfToken}
          onWebsiteChange={handleWebsiteChanged}
          onContactsChange={handleContactsChanged}
          isPast={confirm.calendarClassification === "past"}
          timeZone={confirm.timeZone}
          holdActionsUrl={props.holdActionsUrl}
          holdWeatherUrl={props.holdWeatherUrl}
          ticketingUrl={props.ticketingUrl}
          onSelectTab={selectTab}
          onOpenEditDetails={openEditDetails}
          onLineupChange={handleLineupChanged}
          onToggleRental={handleRentalToggle}
        />
      );
    }
    if (effectiveTab === "tickets") {
      if (tickets.loading) return <TabLoader />;
      if (tickets.error || !tickets.data) {
        return (
          <div className="event-tab-loading" style={{ padding: "48px 16px", textAlign: "center", color: "var(--sl-text-tertiary)" }}>
            Couldn’t load tickets. Refresh to try again.
          </div>
        );
      }
      return <TicketsTab {...tickets.data} />;
    }
    if (effectiveTab === "contacts") {
      return (
        <ContactsTab
          contactsUrl={props.contactsUrl}
          teamId={props.team.id}
          confirmId={confirm.id}
          csrfToken={props.csrfToken}
          canManage={confirm.canEditDetails}
          canShare={Boolean(props.currentUser?.canCreateInvitation)}
          guestRoleId={props.guestRoleId}
          eventLabel={ticketCountsEventLabel(confirm.title, confirm.humanStartDate)}
        />
      );
    }
    if (effectiveTab === "assets") {
      if (assets.loading) return <TabLoader />;
      if (assets.error || !assets.data) {
        return (
          <div className="event-tab-loading" style={{ padding: "48px 16px", textAlign: "center", color: "var(--sl-text-tertiary)" }}>
            Couldn’t load assets. Refresh to try again.
          </div>
        );
      }
      return <AssetsTab data={assets.data} assetsUrl={props.assetsUrl} team={props.team} csrfToken={props.csrfToken} />;
    }
    if (effectiveTab === "show_details") {
      if (showDetails.loading) return <TabLoader />;
      if (showDetails.error || !showDetails.data) {
        return (
          <div className="event-tab-loading" style={{ padding: "48px 16px", textAlign: "center", color: "var(--sl-text-tertiary)" }}>
            Couldn’t load show details. Refresh to try again.
          </div>
        );
      }
      return <ShowDetailsTab data={showDetails.data} csrfToken={props.csrfToken} />;
    }
    if (effectiveTab === "notes") {
      if (notes.loading) return <TabLoader />;
      if (notes.error || !notes.data) {
        return (
          <div className="event-tab-loading" style={{ padding: "48px 16px", textAlign: "center", color: "var(--sl-text-tertiary)" }}>
            Couldn’t load notes. Refresh to try again.
          </div>
        );
      }
      return (
        <Notes
          initialData={notes.data}
          team={props.team}
          csrfToken={props.csrfToken}
          confirm={confirm}
          currentUser={notes.data.currentUser}
          canReadNotes={Boolean(notes.data.currentUser?.can_read_notes)}
          canManageNotes={Boolean(notes.data.currentUser?.can_manage_notes)}
          canManagePermissions={Boolean(notes.data.currentUser?.role && notes.data.currentUser.role.name === "Administrator")}
          canManageNoteTemplates={Boolean(notes.data.currentUser?.can_manage_note_templates)}
          guestTeamMemberships={notes.data.guestTeamMemberships || []}
          promotersWithPermission={notes.data.promotersWithPermission || []}
          onNotesChanged={handleNotesChanged}
        />
      );
    }
    if (effectiveTab === "offers") {
      if (offers.loading) return <TabLoader />;
      if (offers.error || !offers.data) {
        return (
          <div className="event-tab-loading" style={{ padding: "48px 16px", textAlign: "center", color: "var(--sl-text-tertiary)" }}>
            Couldn’t load offers. Refresh to try again.
          </div>
        );
      }
      return (
        <OffersTab
          key={offersTabVersion}
          initialData={offers.data}
          offersUrl={props.offersUrl}
          team={props.team}
          csrfToken={props.csrfToken}
          confirm={confirm}
          onPerformerAttached={markPerformerAttached}
          onOffersChanged={() => [props.settlementsUrl, props.profitAndLossUrl, props.dashboardUrl].forEach(forgetTabData)}
          onDataRefreshed={offers.setData}
        />
      );
    }
    if (effectiveTab === "settlements") {
      if (settlements.loading) return <TabLoader />;
      if (settlements.error || !settlements.data) {
        return (
          <div className="event-tab-loading" style={{ padding: "48px 16px", textAlign: "center", color: "var(--sl-text-tertiary)" }}>
            Couldn’t load settlements. Refresh to try again.
          </div>
        );
      }
      return (
        <SettlementsTab
          initialData={settlements.data}
          team={props.team}
          csrfToken={props.csrfToken}
          confirm={confirm}
        />
      );
    }
    if (effectiveTab === "finances") {
      if (finances.loading) return <TabLoader />;
      if (finances.error || !finances.data) {
        return (
          <div className="event-tab-loading" style={{ padding: "48px 16px", textAlign: "center", color: "var(--sl-text-tertiary)" }}>
            Couldn’t load finances. Refresh to try again.
          </div>
        );
      }
      return (
        <FinancesTab
          initialData={finances.data}
          financesUrl={props.financesUrl}
          rentalInvoicesUrl={props.rentalInvoicesUrl}
          team={props.team}
          csrfToken={props.csrfToken}
          confirm={confirm}
        />
      );
    }
    if (effectiveTab === "marketing") {
      if (marketing.loading) return <TabLoader />;
      if (marketing.error || !marketing.data) {
        return (
          <div className="event-tab-loading" style={{ padding: "48px 16px", textAlign: "center", color: "var(--sl-text-tertiary)" }}>
            Couldn’t load marketing insights. Refresh to try again.
          </div>
        );
      }
      const m = marketing.data;
      return (
        <div className="marketing-insights-root">
          <div className="mi-page">
            <AudienceInsights teamId={m.team.id} confirmId={m.confirmId} venueName={m.venueName} />
            <BuyerLocationCard teamId={m.team.id} confirmId={m.confirmId} />
            <TrafficConversionCard teamId={m.team.id} confirmId={m.confirmId} attributionWindowDays={m.team.attribution_window_days} />
            <SourcesLinksCard teamId={m.team.id} confirmId={m.confirmId} csrfToken={m.csrfToken} />
            <MetaAdSpendSummary teamId={m.team.id} confirmId={m.confirmId} />
            <EventMessages teamId={m.team.id} confirmId={m.confirmId} />
            <ComparableEvents teamId={m.team.id} confirmId={m.confirmId} />
          </div>
        </div>
      );
    }
    if (effectiveTab === "attribution") {
      return <EventAttributionTab key={props.attribution.eventId} {...props.attribution} />;
    }
    if (effectiveTab === "profit_and_loss") {
      if (profitAndLoss.loading) return <TabLoader />;
      if (profitAndLoss.error || !profitAndLoss.data) {
        return (
          <div className="event-tab-loading" style={{ padding: "48px 16px", textAlign: "center", color: "var(--sl-text-tertiary)" }}>
            Couldn’t load profit &amp; loss. Refresh to try again.
          </div>
        );
      }
      return <ProfitAndLossTab data={profitAndLoss.data} />;
    }
    if (effectiveTab === "staffing") {
      if (staffing.loading) return <TabLoader />;
      if (props.staffingUrl && (staffing.error || !staffing.data)) {
        return (
          <div className="event-tab-loading" style={{ padding: "48px 16px", textAlign: "center", color: "var(--sl-text-tertiary)" }}>
            Couldn’t load staffing. Refresh to try again.
          </div>
        );
      }
      return (
        <StaffingTab
          tab={props.staffingTab}
          url={props.staffingUrl}
          data={staffing.data}
          setData={staffing.setData}
        />
      );
    }
    return <TabPlaceholder name={label} />;
  };

  return (
    <div className="event-page">
      <ToastContainer icon={false} />
      <div className="page-container">
      <EventHeader
        title={confirm.title}
        badges={
          <>
            {confirm.calendarClassification && <EventStatusBadge classification={confirm.calendarClassification} />}
            {confirm.classification && <Badge color="brand" size="md" icon={MusicNote01}>{confirm.classification}</Badge>}
          </>
        }
        meta={headerMeta}
        actions={
          <>
            {props.teamMembershipId && (
              <PageLayoutSelector
                layout="tabbed"
                teamId={props.team.id}
                confirmId={confirm.id}
                csrfToken={props.csrfToken}
              />
            )}
            <Button
              variant="secondary"
              icon={Edit01}
              onClick={confirm.canEditDetails ? openEditDetails : () => window.location.assign(editDetailsUrl)}
              disabled={editLoading}
            >
              {editLoading ? "Loading…" : "Edit details"}
            </Button>
            {editOpen && editProps && (
              <span className="event-edit-details-host">
                <Suspense fallback={null}>
                  <EventDetailsApp
                    {...editProps}
                    openOnMount
                    onClose={() => setEditOpen(false)}
                    confirmSaved={() => window.location.reload()}
                    financeItemsChanged={() => {}}
                  />
                </Suspense>
              </span>
            )}
            <ActionsMenu
              teamId={props.team.id}
              confirm={confirm}
              currentUser={props.currentUser}
              csrfToken={props.csrfToken}
              actionDataUrl={props.actionDataUrl}
              duplicateUrl={props.duplicateUrl}
              holdActionsUrl={props.holdActionsUrl}
            />
          </>
        }
        edited={<LastEditIndicator ago={confirm.lastVersionAgo} time={confirm.lastVersionTime} versions={confirm.versions} />}
      >
        {genres.length > 0 && (
          <EventHeaderField icon={MagicWand02} label="Genres">
            {genres.map((g) => (
              <Tag key={g} size="sm" dot>{g}</Tag>
            ))}
          </EventHeaderField>
        )}
        <TagsField
          teamId={props.team.id}
          taggableId={confirm.id}
          initialTags={tags}
          canManage={confirm.canManageTags}
        />
      </EventHeader>

        {(confirm.eventSeries || []).map((series) => (
          <EventSeriesBanner key={series.id} series={series} teamId={props.team.id} csrfToken={props.csrfToken} calendarEventId={confirm.id} startsAt={confirm.startTime} />
        ))}

        <OfferPerformerBanner
          offers={flaggedOffers}
          performers={missingPerformer?.performers || []}
          canManage={Boolean(missingPerformer?.canManage)}
          onAttach={attachFromBanner}
          onReview={() => selectTab("offers")}
        />

        <EventTabNav tabs={tabs} active={effectiveTab} onSelect={selectTab} />

        <div className="event-tab-content">
          <ErrorBoundary FallbackComponent={TabErrorFallback} resetKeys={[effectiveTab]}>
            <Suspense fallback={<TabLoader />}>
              {renderTab()}
            </Suspense>
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
};

EventPage.propTypes = {
  confirm: PropTypes.shape({
    id: PropTypes.number,
    type: PropTypes.string,
    isRental: PropTypes.bool,
    calendarClassification: PropTypes.string,
    mayCancel: PropTypes.bool,
    mayUncancel: PropTypes.bool,
    mayPostpone: PropTypes.bool,
    mayUnpostpone: PropTypes.bool,
    title: PropTypes.string,
    humanStartDate: PropTypes.string,
    timeZone: PropTypes.string,
    startTime: PropTypes.string,
    doorTime: PropTypes.string,
    venueName: PropTypes.string,
    venueLocation: PropTypes.string,
    venueConfig: PropTypes.string,
    buyer: PropTypes.string,
    promoter: PropTypes.string,
    eventTemplate: PropTypes.string,
    ageRestriction: PropTypes.string,
    status: PropTypes.string,
    classification: PropTypes.string,
    genres: PropTypes.arrayOf(PropTypes.string),
    tags: PropTypes.arrayOf(PropTypes.string),
    lastVersionAgo: PropTypes.string,
    lastVersionTime: PropTypes.string,
    versions: PropTypes.array,
    eventSeries: PropTypes.array,
    canEditDetails: PropTypes.bool,
    canManageTags: PropTypes.bool,
    holdDates: PropTypes.array,
  }).isRequired,
  editDetailsUrl: PropTypes.string.isRequired,
  teamMembershipId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  editDetailsPropsUrl: PropTypes.string,
  dashboardUrl: PropTypes.string,
  ticketsUrl: PropTypes.string,
  contactsUrl: PropTypes.string,
  guestRoleId: PropTypes.number,
  team: PropTypes.shape({ id: PropTypes.number }),
  assetsUrl: PropTypes.string,
  showDetailsUrl: PropTypes.string,
  notesUrl: PropTypes.string,
  offersUrl: PropTypes.string,
  settlementsUrl: PropTypes.string,
  financesUrl: PropTypes.string,
  rentalInvoicesUrl: PropTypes.string,
  marketingUrl: PropTypes.string,
  profitAndLossUrl: PropTypes.string,
  holdActionsUrl: PropTypes.string,
  actionDataUrl: PropTypes.string,
  duplicateUrl: PropTypes.string,
  staffingTab: PropTypes.oneOf(["schedule", "hidden"]),
  staffingUrl: PropTypes.string,
  currentUser: PropTypes.object,
  csrfToken: PropTypes.string,
  attribution: PropTypes.shape({
    teamId: PropTypes.number,
    eventId: PropTypes.number,
    defaultRange: PropTypes.shape({ start: PropTypes.string, end: PropTypes.string }),
    defaultWindowDays: PropTypes.number,
    trackingStartedAt: PropTypes.string,
    maxWindowDays: PropTypes.number,
  }),
  offersMissingPerformer: PropTypes.shape({
    offers: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    performers: PropTypes.arrayOf(PropTypes.object),
    canManage: PropTypes.bool,
  }),
};

export default EventPage;
